import React, { useEffect, useMemo, useState } from "react";
import Paper from "@mui/material/Paper";
import { DataGrid } from "@mui/x-data-grid";

/**
 * Standard data table for every tabular Admin view.
 *
 * Keeps pagination, density, row hover/focus, empty/loading states and the
 * light/dark admin visual system consistent without forcing card/calendar
 * screens into an inappropriate table layout.
 */
export default function AdminDataGrid({
  rows = [],
  columns = [],
  loading = false,
  isLight = true,
  height = 560,
  rowHeight = 56,
  fitHeight = true,
  pageSize = 10,
  pageSizeOptions = [5, 10, 25, 50],
  getRowId,
  onRowClick,
  checkboxSelection = false,
  emptyMessage = "No records found.",
  sx,
  ...gridProps
}) {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize });
  const safeRows = Array.isArray(rows) ? rows : [];
  const totalPages = Math.max(1, Math.ceil(safeRows.length / Math.max(1, paginationModel.pageSize)));
  const visibleRowCount = Math.max(0, Math.min(
    paginationModel.pageSize,
    safeRows.length - paginationModel.page * paginationModel.pageSize
  ));
  const footerHeight = gridProps.hideFooter ? 0 : 52;
  const calculatedHeight = typeof height === "number" && fitHeight
    ? Math.min(height, Math.max(132 + footerHeight, 44 + footerHeight + visibleRowCount * rowHeight + 2))
    : height;

  useEffect(() => {
    setPaginationModel((current) => current.pageSize === pageSize ? current : { page: 0, pageSize });
  }, [pageSize]);

  useEffect(() => {
    if (paginationModel.page >= totalPages) {
      setPaginationModel((current) => ({ ...current, page: Math.max(0, totalPages - 1) }));
    }
  }, [paginationModel.page, totalPages]);

  const gridSx = useMemo(() => ({
    border: 0,
    color: isLight ? "#0f1a2b" : "#E8F0F7",
    background: isLight ? "#FFFFFF" : "#0f172a",
    "& .MuiDataGrid-columnHeaders": {
      minHeight: "44px !important",
      maxHeight: "44px !important",
      background: isLight ? "linear-gradient(180deg,#EDF6FF 0%,#DCEEFF 100%)" : "rgba(37,99,235,.18)",
      borderBottom: isLight ? "1px solid #B9D8F6" : "1px solid #315879",
    },
    "& .MuiDataGrid-columnHeaderTitle": { fontSize: 12, fontWeight: 800, color: isLight ? "#123B69" : "#DBEAFE" },
    "& .MuiDataGrid-cell": {
      borderColor: isLight ? "#eef2f7" : "#1E3448",
      fontSize: 12.5,
      outline: "none !important",
      display: "flex",
      alignItems: "center",
      paddingTop: 5,
      paddingBottom: 5,
      lineHeight: 1.35,
    },
    "& .MuiDataGrid-cellContent": { whiteSpace: "normal", overflow: "hidden", textOverflow: "ellipsis" },
    "& .MuiDataGrid-row": { cursor: onRowClick ? "pointer" : "default" },
    "& .MuiDataGrid-row:nth-of-type(even)": { background: isLight ? "rgba(248,250,252,.85)" : "rgba(255,255,255,.02)" },
    "& .MuiDataGrid-row:hover": { background: isLight ? "rgba(15,157,143,.07)" : "rgba(255,255,255,.05)" },
    "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover": { background: isLight ? "rgba(8,117,225,.10)" : "rgba(147,197,253,.12)" },
    "& .MuiDataGrid-footerContainer": { minHeight: 52, borderTop: isLight ? "1px solid #e3e8ef" : "1px solid #1E3448" },
    "& .MuiDataGrid-overlay": { color: isLight ? "#64748B" : "#94A3B8" },
    "& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton": { color: isLight ? "#64748B" : "#94A3B8" },
    "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within": { outline: "2px solid #60A5FA !important", outlineOffset: -2 },
    ...sx,
  }), [isLight, onRowClick, sx]);

  return (
    <Paper
      elevation={0}
      sx={{
        height: calculatedHeight,
        width: "100%",
        overflow: "hidden",
        borderRadius: "10px",
        border: isLight ? "1px solid #e3e8ef" : "1px solid #1E3448",
        background: isLight ? "#FFFFFF" : "#0f172a",
      }}
    >
      <DataGrid
        rows={safeRows}
        columns={columns}
        loading={loading}
        getRowId={getRowId}
        onRowClick={onRowClick}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={pageSizeOptions}
        rowHeight={rowHeight}
        density="standard"
        disableColumnSelector
        disableColumnFilter
        slotProps={{ noRowsOverlay: { message: emptyMessage } }}
        sx={gridSx}
        {...gridProps}
      />
    </Paper>
  );
}
